import { useRef, useState } from "react";

/**
 * Shared open/close/in-flight state for the "a home_collage_order slot is
 * already taken -- reassign it?" confirm flow (Redmine #266343), reused
 * across all six Masterlist Basic forms (Film, Personality, Location,
 * Theme, Institution, EventMasterlist). Only this bookkeeping is identical
 * across all six -- the actual retry submit (which API function to call,
 * how to shape that form's own payload) differs per form and stays in each
 * form's own handleSubmit, passed in to confirm() as onConfirm.
 */
export const useHomeCollageReassign = () => {
  const [conflict, setConflict] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const cancelRef = useRef();
  // The calling form's own retry submit (resends the save with
  // confirm_home_collage_order_reassign: true merged in) -- stored in a ref
  // rather than state since it's a function, not render-affecting data, and
  // only ever read inside confirm()'s event handler.
  const retryRef = useRef(null);

  const open = (conflictData, onConfirm) => {
    retryRef.current = onConfirm;
    setConflict(conflictData);
  };

  const close = () => {
    if (isConfirming) return; // don't allow dismissing mid-request
    setConflict(null);
    retryRef.current = null;
  };

  // Left open (conflict not cleared) on failure so the admin can retry or
  // cancel rather than losing the context -- the retry itself is expected to
  // show its own error toast, so this just logs rather than re-surfacing it,
  // and swallows rather than letting it become an unhandled rejection at
  // whatever onClick handler called confirm().
  const confirm = async () => {
    if (!retryRef.current) return;
    setIsConfirming(true);
    try {
      await retryRef.current();
      setConflict(null);
      retryRef.current = null;
    } catch (error) {
      console.error("home collage reassign retry failed", error);
    } finally {
      setIsConfirming(false);
    }
  };

  return {
    conflict,
    isOpen: !!conflict,
    isConfirming,
    cancelRef,
    open,
    close,
    confirm,
  };
};
